import axios from 'axios'
import emitter from '@/helper/emitter'
import WebSocketClient from '@/helper/websocket'
import LazyLoad from 'vanilla-lazyload'
import tippy from 'tippy.js'
import { Buffer } from 'buffer'
import * as object from './object'
import printJS from 'print-js'
import storage from './storage'
import { useToast } from 'vue-toast-notification'
import * as moment from 'moment-timezone'

let toast = useToast()
let prevDropdownElement

let self = {
  loading: false,
  options: Object.freeze(window.AppOptions),
  components: {},
  storage,
  ...object,
  axios: {
    instance: axios.create(),
    controllers: [],
    constructor() {
      this.instance.interceptors.request.use(
        (config) => {
          const controller = new AbortController()
          config.signal = controller.signal
          this.controllers.push(controller)
          return config
        },
        (error) => {
          return Promise.reject(error)
        }
      )
      this.instance.interceptors.response.use(
        (response) => {
          this.controllers = this.controllers.filter((c) => c.signal !== response.config.signal)
          return response
        },
        (error) => {
          this.controllers = this.controllers.filter((c) => c.signal !== error.config.signal)
          return Promise.reject(error)
        }
      )
    },
    destroyRequests() {
      this.controllers.forEach((controller) => controller.abort())
      this.controllers = []
    }
  },
  data: {
    encode(object = {}) {
      try {
        if (object == '') {
          return object
        }
        return Buffer.from(encodeURIComponent(JSON.stringify(object))).toString('base64')
      } catch {
        return ''
      }
    },
    decode(str, def = {}) {
      try {
        if (str == '') {
          return def
        }
        return JSON.parse(decodeURIComponent(Buffer.from(str, 'base64').toString('ascii')))
      } catch {
        return {}
      }
    }
  },
  cookie: {
    get(name, decoded = true) {
      let nameEQ = name + '='
      let ca = document.cookie.split(';')
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i].replace(/^\s+/g, '')
        if (c.indexOf(nameEQ) == 0) {
          return decoded ? self.data.decode(c.substring(nameEQ.length, c.length)) : c.substring(nameEQ.length, c.length)
        }
      }
      return null
    },
    set(name, value, days) {
      let expires = ''
      if (days) {
        let date = new Date()
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
        expires = '; expires=' + date.toUTCString()
      }
      document.cookie = name + '=' + self.data.encode(value) + expires + '; path=/'
    },
    erase(name) {
      document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
    }
  },
  sessionStorage: {
    get(name, decoded = true) {
      let value = window.sessionStorage.getItem(name)
      if (value) {
        return decoded ? self.data.decode(value) : value
      }
      return null
    },
    set(name, value) {
      window.sessionStorage.setItem(name, self.data.encode(value))
    },
    erase(name) {
      window.sessionStorage.removeItem(name)
    }
  },
  localStorage: {
    get(name) {
      return self.data.decode(window.localStorage.getItem(name))
    },
    set(name, value) {
      window.localStorage.setItem(name, self.data.encode(value))
    },
    erase(name) {
      window.localStorage.removeItem(name)
    }
  },
  event: {
    create(name, object) {
      let event = new CustomEvent(`master-tool.${name}`, {
        bubbles: true,
        cancelable: true,
        detail: object
      })
      document.dispatchEvent(event)
    },
    listener(name, fn) {
      document.addEventListener(`master-tool.${name}`, fn)
    },
    remove(name, fn) {
      document.removeEventListener(`master-tool.${name}`, fn)
    }
  },
  modal: {
    create(name, data, $el) {
      let event = new CustomEvent('MasterTool.modal', {
        bubbles: true,
        cancelable: true,
        detail: {
          name,
          data,
          $el
        }
      })
      document.dispatchEvent(event)
    },
    listener(fn) {
      document.addEventListener('MasterTool.modal', fn)
    }
  },
  construct() {
    // if (!window.webSocket) window.webSocket = new WebSocketClient()
    // this.webSocket = window.webSocket
    let components = {}
    Object.entries(import.meta.glob('./../**/*.vue', { eager: true })).forEach(([key, component]) => {
      components[
        key.replace(/^\.\./, '').replace(/\/(\w)|[_-](\w)/g, (match, p1, p2) => {
          if (p1) return `/${p1.toUpperCase()}`
          if (p2) return p2.toUpperCase()
          return match
        })
      ] = component.default || component
    })
    this.components = Object.freeze(components)
    this.tip()
    this.lazyload()
    let mixins = {
      pascalCase: this.pascalCase,
      mergeLeft: this.mergeLeft
    }
    Object.entries(mixins).forEach(([name]) => {
      if (_?.[name]) console.log(`Function [${name}] already exists`)
    })
    _.mixin(mixins)

    document.addEventListener('click', self.dropdown)
    emitter.on('close-all-dropdown', self.closeAllDropdown)
    this.axios.constructor()
  },
  requireExists(path) {
    return this.requireOrNull(path) !== null
  },
  requireOrNull(path) {
    path = path.replace(/\/(\w)|[_-](\w)/g, (match, p1, p2) => {
      if (p1) return `/${p1.toUpperCase()}`
      if (p2) return p2.toUpperCase()
      return match
    })
    if (this.components[path] != undefined) {
      try {
        return this.components[path]
      } catch (e) {
        if (e.code === 'MODULE_NOT_FOUND') return null
        throw e
      }
    } else {
      return Object.values(this.requiresOrNull(path, false) || []).shift() || null
    }
  },
  requiresOrNull(path, pascalPath = true) {
    if (pascalPath) {
      path = path.replace(/\/(\w)|[_-](\w)/g, (match, p1, p2) => {
        if (p1) return `/${p1.toUpperCase()}`
        if (p2) return p2.toUpperCase()
        return match
      })
    }
    let results = {}
    let regex = new RegExp(path, 'i')
    try {
      for (const [key, component] of Object.entries(this.components)) {
        if (regex.test(key)) {
          results[key] = component
        }
      }
      return results
    } catch (e) {
      if (e.code === 'MODULE_NOT_FOUND') return null
      throw e
    }
  },
  randStr(length) {
    let characters = 'abcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    const charactersLength = characters.length
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength))
    }
    return result
  },
  timestampToDate(time) {
    return new Date(time * 1000).toLocaleDateString()
  },
  timestampToTime(time) {
    return new Date(time * 1000).toLocaleTimeString(undefined, {
      hour12: false
    })
  },
  timestampToDateTime(time, br, timeZone = null) {
    if (!time) return null
    br = br || false
    const date = new Date(time * 1000)
    let dateTimeString = null
    if (!timeZone) {
      dateTimeString = `${date.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })}${br === true ? '<br>' : ' '}<strong>${date.toLocaleDateString()}</strong>`
    } else {
      dateTimeString = `${moment.tz(date, timeZone).format('HH:mm')}${br === true ? '<br>' : ' '}<strong>${moment.tz(date, timeZone).format('Lz')}</strong>`
    }

    return dateTimeString
  },
  getObjectVal(object, keys) {
    let keyArray = []
    try {
      if (_.isArray(keys)) {
        keys = keys.join('.').replace(/^\.|\.$/g, '')
      }
      keyArray = keys.split(/\s*\.\s*/g)
    } catch (e) {
      console.log(e, keys, object)
    }
    return this.getObjectValueByArrayKeys(
      object,
      keyArray.filter((x) => ![''].includes(x) && x !== '')
    )
  },
  getObjectValueByArrayKeys(object, keys) {
    if (_.isArray(keys)) {
      let key = keys.shift()
      if (!_.isObjectLike(object)) {
        object = {}
      }
      if (keys.length > 0) {
        if (_.isObjectLike(object[key])) {
          return this.getObjectValueByArrayKeys(object[key], keys)
        } else {
          return null
        }
      } else if (object[key] != undefined) {
        return object[key]
      } else {
        return null
      }
    }
  },
  setObjectVal(object, keys, value) {
    let keyArray = []
    try {
      if (_.isArray(keys)) {
        keys = keys.join('.').replace(/^\.|\.$/g, '')
      }
      keyArray = keys.split(/\s*\.\s*/g)
    } catch (e) {
      console.log(e, keys, object)
    }
    return this.setObjectValueByArrayKeys(
      object,
      keyArray.filter((x) => ![''].includes(x) && x !== ''),
      value
    )
  },
  setObjectValueByArrayKeys(object, keys, value) {
    if (_.isArray(keys)) {
      let key = keys.shift()
      if (!_.isObjectLike(object) || (_.isArray(object) && !(parseFloat(key) == parseInt(key) && 0 <= parseInt(key) <= object.length))) {
        object = {}
      }
      if (keys.length > 0) {
        if (!_.isObjectLike(object[key])) {
          object[key] = {}
        }
        object[key] = this.setObjectValueByArrayKeys(object[key], keys, value)
      } else if (key) {
        object[key] = value
      } else {
        object = value
      }
    }
    return object
  },
  mergeLeft(...objects) {
    let result = objects[0]
    let index
    for (index = 1; index < objects.length; index++) {
      if (_.isObjectLike(objects[index])) {
        if (objects[index] instanceof Date || objects[index] instanceof File || objects[index] instanceof Blob) {
          result = objects[index]
        } else {
          if (!_.isObjectLike(result)) {
            result = !_.isArray(objects[index]) || (objects[0] && !_.isArray(objects[0])) ? {} : []
          }
          Object.entries(objects[index]).forEach(([key, value]) => {
            if (_.isObjectLike(value)) {
              result[key] = this.mergeLeft(result[key], value)
            } else {
              result[key] = value
            }
          })
        }
      }
    }
    return result
  },
  filter(object, callback) {
    return object
      ? Object.keys(object)
          .filter((key) => callback({ item: object[key], key }))
          .reduce((next, key) => ((next[key] = object[key]), next), {})
      : {}
  },
  map(object, callback) {
    return object ? Object.keys(object).reduce((next, key) => ((next[key] = callback({ item: object[key], key })), next), {}) : {}
  },
  reduce(object, callback, def) {
    return object ? Object.keys(object).reduce((next, key) => (next = callback({ item: object[key], key, next })), def || null) : {}
  },
  only(structure, object) {
    let values = {}
    if (_.isArray(structure)) {
      structure.forEach((key) => {
        let keys = key.split('.')
        this.setObjectVal(values, [...keys], this.getObjectVal(object, [...keys]))
      })
    } else {
      Object.entries(structure).forEach(([source, dest]) => {
        this.setObjectVal(values, dest.split('.'), this.getObjectVal(object, source.split('.')))
      })
    }
    return values
  },
  tip() {
    setInterval(() => {
      document.body.querySelectorAll('.has-tip:not([data-title=""]):not(.tips-attached)').forEach((el) => {
        tippy(el, {
          content: el.getAttribute('data-title'),
          allowHTML: true
        })
        el.classList.add('tips-attached')
      })
    }, 1000)
  },
  lazyload() {
    let time
    const lazyLoad = new LazyLoad({
      elements_selector: '.lazyload',
      unobserve_entered: true,
      callback_loading(el) {
        el?.parentElement?.classList?.add('lazy-loading')
        el?.parentElement?.classList?.remove('lazy-loaded')
        el?.parentElement?.classList?.remove('lazy-error')
      },
      callback_loaded(el) {
        el?.parentElement?.classList?.remove('lazy-loading')
        el?.parentElement?.classList?.add('lazy-loaded')
        el?.parentElement?.classList?.remove('lazy-error')
      },
      callback_error(el) {
        el?.parentElement?.classList?.remove('lazy-loading')
        el?.parentElement?.classList?.remove('lazy-loaded')
        el?.parentElement?.classList?.add('lazy-error')
      }
    })
    emitter.on('lazyload', () => {
      if (time) {
        clearTimeout(time)
      }
      time = setTimeout(() => {
        lazyLoad.update()
      }, 100)
    })
  },
  dropdown(e) {
    let parent
    self.closeAllDropdown()
    if (e.target.matches('[data-toggle="dropdown"]') || e.target.closest('[data-toggle="dropdown"]')) {
      let menu, isExpanded
      let el = e.target.matches('[data-toggle="dropdown"]')
      if (!el.nodeType) {
        el = e.target.closest('[data-toggle="dropdown"]')
      }
      if (!el.isEqualNode(prevDropdownElement)) {
        prevDropdownElement = el
        isExpanded = el.getAttribute('aria-expanded') == 'true' ? 1 : 0
        parent = el.closest('.dropdown') instanceof HTMLElement ? el.closest('.dropdown') : el.parentElement
        menu = parent.querySelector('.dropdown-menu')
        el.setAttribute('aria-expanded', isExpanded ? 'false' : 'true')
        if (menu) {
          if (isExpanded) {
            menu.classList.remove('show')
          } else {
            menu.classList.add('show')
          }
        }
      } else {
        prevDropdownElement = null
      }
    } else {
      prevDropdownElement = null
    }
  },
  closeAllDropdown() {
    let elements = document.querySelectorAll('[data-toggle="dropdown"]')
    if (elements.length > 0) {
      let parent
      elements.forEach((el) => {
        el.setAttribute('aria-expanded', 'false')
        parent = el.closest('.dropdown') instanceof HTMLElement ? el.closest('.dropdown') : el.parentElement
        parent.querySelector('.dropdown-menu').classList.remove('show')
      })
    }
  },
  notify(object) {
    let msgHtml = []
    if (_.isObjectLike(object.text)) {
      Object.values(object.text).forEach((items) => {
        if (_.isObjectLike(items)) {
          Object.values(items).forEach((item) => {
            msgHtml.push(`<p class="mb-0">${item}</p>`)
          })
        }
      })
    } else {
      msgHtml.push(object.text)
    }
    if (msgHtml.length > 0) {
      toast.open({
        message: msgHtml.join(''),
        type: object.status || 'success',
        position: 'top-right',
        duration: 5000
      })
    }
  },
  getOffset($el, $parent) {
    let elOffset = $el.getBoundingClientRect()
    if (!_.isUndefined($parent)) {
      let parentOffset = $parent.getBoundingClientRect()
      return {
        top: elOffset.top - parentOffset.top,
        left: elOffset.left - parentOffset.left
      }
    }
    return {
      top: elOffset.top,
      left: elOffset.left
    }
  },
  isLoading($el, args) {
    args = Object.assign(
      {
        loading: true,
        freeze: document.body,
        preload: false,
        uid: null
      },
      args
    )

    let $loading = null
    let uid = null
    if (args.loading == true) {
      uid = 'is-loading-' + (Math.random() + 1).toString(36).substring(7) + Date.now()
      $loading = document.createElement('div')
      $loading.setAttribute('data-type', 'is-loadings')
      $loading.setAttribute('id', uid)
    } else {
      $loading = document.body.querySelector(`#${args.uid}`)
    }

    const setLoadingCss = () => {
      if (_.isObjectLike($loading) && $loading.nodeType === 1) {
        $loading.style.position = 'fixed'
        $loading.style['z-index'] = 99999
        if (args.preload) {
          $loading.style.width = '100%'
          $loading.style.height = '100%'
          $loading.style.left = 0
          $loading.style.top = 0
          $loading.style.background = '#ffffff'
        } else {
          let offset = $el.getBoundingClientRect()
          $loading.style.width = $el.offsetWidth + 'px'
          $loading.style.height = Math.min(window.innerHeight - Math.max(0, offset.top), $el.offsetHeight) + 'px'
          $loading.style.left = offset.left + 'px'
          $loading.style.top = Math.max(0, offset.top) + 'px'
        }
      }
      if ($loading.innerHTML == '') {
        window.removeEventListener('scroll', setLoadingCss)
        window.removeEventListener('resize', setLoadingCss)
      }
    }

    if (args.loading == true) {
      let hasLoading = false
      if (!args.preload) {
        if (_.isObjectLike($el) && $el.nodeType === 1) {
          $el.$loading = $loading
          $el.classList.add('filter-blur-5')
          hasLoading = true
          setLoadingCss($el, $loading)
          window.addEventListener('resize', setLoadingCss)
          window.addEventListener('scroll', setLoadingCss)
        }
      } else {
        hasLoading = true
        setLoadingCss(document.body, $loading)
      }

      if (hasLoading) {
        $loading.innerHTML = '<div class="loadingio-spinner-ellipsis"><div class="ldio"><div></div><div></div><div></div><div></div><div></div></div></div>'
        document.body.append($loading)
      }
    } else {
      if (_.isObjectLike($el) && $el.nodeType === 1) {
        $el.classList.remove('filter-blur-5')
      }
      if (_.isObjectLike($loading) && $loading.nodeType === 1) {
        $loading.innerHTML = ''
        $loading.remove()
        $loading = null
      }
    }

    if (args.loading) {
      return uid
    }
  },
  removeAllLoading() {
    document.body.querySelectorAll('[data-type="is-loadings"]').forEach(function (e) {
      e.remove()
    })
  },
  buildFormData(formData, data, parentKey) {
    if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File) && !(data instanceof Blob)) {
      Object.keys(data).forEach((key) => {
        if (key != '_keys') {
          formData = this.buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key)
        }
      })
    } else if (data instanceof File || data instanceof Blob) {
      formData.append(parentKey, data)
    } else {
      formData.append(parentKey, data || '')
    }
    return formData
  },
  objectToFormData(object) {
    return this.buildFormData(new FormData(), object)
  },
  async request(uri, data, $el, callback, config, args) {
    return new Promise((resolve, reject) => {
      args = Object.assign(
        {
          uploadFile: false,
          preload: false,
          baseUrl: this.options.apiUrl,
          contentType: 'json',
          force: false
        },
        args
      )
      if (!this.loading || args?.force == true) {
        if (args?.force != true) {
          this.loading = true
        }
        try {
          config = Object.assign(
            {
              method: 'post',
              url: /^https?:\/\//.test(uri) ? uri : args.baseUrl + `/${uri}`.replace('//', '/')
            },
            config
          )

          let loadingUid = this.isLoading($el, {
            loading: true,
            freeze: $el,
            preload: args.preload
          })

          if (config.method == 'post') {
            if (args.contentType == 'params') {
              config.data = this.objectToFormData(data)
            } else {
              config.data = data
            }
          } else {
            config.params = data
          }

          this.axios
            .instance(config)
            .then((response) => {
              if (args.force != true) {
                this.loading = false
              }
              try {
                if (_.isObjectLike(callback) && _.isFunction(callback.done)) {
                  callback.done(response.data)
                } else if (_.isFunction(callback)) {
                  callback(response.data)
                }
                if (_.isObjectLike(callback) && _.isFunction(callback.finally)) {
                  callback.finally(response.data)
                }
              } catch (e) {
                console.log(e)
              }

              resolve(response.data)
              if (_.isObjectLike(response.data) && !_.isUndefined(response.data.msg) && !_.isUndefined(response.data.status)) {
                this.notify({
                  text: response.data.msg,
                  status: response.data.status
                })
              }

              this.isLoading($el, {
                loading: false,
                freeze: 'el',
                preload: args.preload,
                uid: loadingUid
              })
            })
            .catch((error) => {
              if (args.force != true) {
                this.loading = false
              }

              let response = Object.assign({}, error?.response?.data || {})
              if (response?.redirect) {
                window.location.replace(response.redirect)
              }
              if (_.isString(response?.trace_url)) {
                if (this.options.dump && /^(?:\w+:)?\/\/([^\s\\.]+\.\S{5}|localhost[\\:?\d]*)\S*$/.test(response.trace_url)) {
                  console.log(response.trace_url)
                }
              }

              try {
                if (_.isFunction(callback?.error)) {
                  callback.error(response)
                } else if (_.isFunction(callback)) {
                  callback(response)
                }
                if (_.isFunction(callback?.finally)) {
                  callback.finally(response)
                }
              } catch (e) {
                console.error(e)
              }

              this.isLoading($el, {
                loading: false,
                freeze: 'el',
                preload: args.preload,
                uid: loadingUid
              })

              if (!axios.isCancel(error)) {
                reject(error)
                console.error(error)
              }

              if (response?.msg && response?.status) {
                this.notify({
                  text: response.msg,
                  status: response.status
                })
              }
            })
        } catch (e) {
          reject(e)
          console.error(e)
        }
      } else {
        reject(null)
      }
    })
  },
  async forceGet(uri, data, $el, callback, config, args) {
    return self.get(uri, data, $el, callback, config, Object.assign({}, args, { force: true }))
  },
  async get(uri, data, $el, callback, config, args) {
    config = Object.assign({}, config, {
      method: 'get'
    })
    return this.request(uri, data, $el, callback, config, args)
  },
  async forcePost(uri, data, $el, callback, config, args) {
    return self.post(uri, data, $el, callback, config, Object.assign({}, args, { force: true }))
  },
  async post(uri, data, $el, callback, config, args) {
    config = Object.assign({}, config, {
      method: 'post'
    })
    return this.request(uri, data, $el, callback, config, args)
  },
  linkify(text) {
    var urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\\/%?=~_|!:,.;]*[-A-Z0-9+&@#\\/%=~_|])/gi
    return text.replace(urlRegex, function (url) {
      return '<a href="' + url + '" target="_blank">' + url + '</a>'
    })
  },
  pascalCase(str) {
    if (!str) return ''
    return str
      .toLowerCase()
      .replace(/[-_.]+/g, ' ')
      .replace(/[^\w\s]/g, '')
      .replace(/\s+(.)(\w*)/g, ($1, $2, $3) => `${$2.toUpperCase() + $3}`)
      .replace(/\w/, (s) => s.toUpperCase())
  },
  getExtensionFromString(str) {
    const match = String(str).match(/\.([a-zA-Z0-9]+)(?=[?#]|$)/)
    return match?.[1]?.toLowerCase() || null
  },
  getFileNameFromString(str) {
    return decodeURIComponent(new URL(str).pathname.split('/').pop())
  },
  getNameFromString(str) {
    return decodeURIComponent(new URL(str).pathname.split('/').pop().split('.').slice(0, -1).join('.'))
  },
  addQueryParam(baseUrl, params) {
    try {
      if (!baseUrl) return null
      let Url = new URL(baseUrl, window.location.origin)
      let urlParams = new URLSearchParams(Url.search)
      for (const key in params) {
        if (params[key] !== undefined) {
          urlParams.set(key, params[key])
        }
      }
      Url.search = urlParams.toString()
      return Url.toString().replace(window.location.origin, '')
    } catch {
      return null
    }
  },
  getLink(str) {
    return _.isString(str) ? str.match(/(?![^<]*>|[^<>]*<\/)((https?:)\/\/[a-z0-9%&#=~@#$*!.\\/\-?_]+:*[a-z0-9%&#=~@#$*!.\\/\-?_]*)/gi) : str
  },
  autoLink(str) {
    return _.isString(str) ? str.replace(/(?![^<]*>|[^<>]*<\/)((https?:)\/\/[a-z0-9%&#=~@#$*!.\\/\-?_]+:*[a-z0-9%&#=~@#$*!.\\/\-?_]*)/gi, '<a href="$1" target="_blank">$1</a>') : str
  },
  print({ html, size = 'A4', style = '', callback = null }) {
    printJS({
      printable: `<div id="container">${html}</div>`,
      type: 'raw-html',
      style: `@page{margin:0!important,size:${size}}body{box-sizing:border-box;transform:scale(var(--scale-factor));transform-origin:0 0;margin:0!important;padding:0!important}body *{box-sizing:border-box}.page-break{page-break-after:always;display:block:width:1px;height:1px}.d-inline-block{display:inline-block}${style}`,
      onError(e) {
        if (_.isObjectLike(callback) && _.isFunction(callback.error)) {
          callback.error({
            status: 'error',
            e
          })
        }
      },
      onIncompatibleBrowser() {
        if (_.isObjectLike(callback) && _.isFunction(callback.error)) {
          callback.error({
            status: 'error'
          })
        }
      },
      onPrintDialogClose() {
        if (_.isObjectLike(callback) && _.isFunction(callback.done)) {
          callback.done({
            status: 'success'
          })
        } else if (_.isFunction(callback)) {
          callback({
            status: 'success'
          })
        }
      }
    })
  },
  injectScript(attributes) {
    return new Promise((resolve, reject) => {
      let script = document.createElement('script')
      script = Object.assign(script, attributes)
      script.onload = () => {
        resolve(true)
      }
      script.onerror = () => {
        reject(new Error(`Failed to load: ${attributes.src}`))
      }
      document.body.appendChild(script)
    })
  },
  isObjectId(id) {
    return /^[a-fA-F0-9]{24}$/.test(id)
  },
  downloadFile({ url = null, name = null, callback = {} }) {
    return new Promise((resolve, reject) => {
      callback?.start && callback?.start()
      self
        .forceGet(url, null, null, null, {
          headers: {
            'Content-Type': 'application/octet-stream'
          },
          responseType: 'blob',
          onDownloadProgress: (event) => {
            const percent = Math.round((event.loaded * 100) / event.total)
            callback?.progressed && callback?.progressed(percent)
            console.log(`Processed: ${percent}%`)
          }
        })
        .then((response) => {
          const blob = new Blob([response])
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = name ? `${name}.${self.getExtensionFromString(url)}` : self.getFileNameFromString(url)
          link.click()
          URL.revokeObjectURL(link.href)
          callback?.done && callback?.done(blob)
          callback?.finally && callback?.finally(blob)
          resolve(blob)
        })
        .catch((err) => {
          alert('Error download file')
          callback?.error && callback?.error(err)
          callback?.finally && callback?.finally(err)
          reject(err)
          console.error('error', err)
        })
    })
  },
  toPascalCase(str) {
    if (!str) return ''
    return str
      .toLowerCase()
      .replace(/[-_]+/g, ' ')
      .replace(/[^\w\s]/g, '')
      .replace(/\s+(.)(\w*)/g, ($1, $2, $3) => `${$2.toUpperCase() + $3}`)
      .replace(/\w/, (s) => s.toUpperCase())
  },
  captureException(error, payload, severity = 'error') {
    console.error(`[${severity}]`, error, payload)
  },
  captureMessage(message, payload, severity = 'error') {
    console.error(`[${severity}]`, message, payload)
  }
}
export default self
